import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Icon, Text, Title } from "components/common";
import PageLayout from "components/common/pageLayout/pageLayout";
import FollowButton from "components/custom/followButton";
import DemoDataToggle from "components/custom/demoDataToggle";
import { UserAvatar } from "components/custom/userListRow";
import { useLazySearchPeopleQuery } from "features/community/apis";
import { demoPeople } from "features/community/mockData/social";
import { getUserProfilePath } from "constants/app-paths";
import styles from "./People.module.less";

const ROLES = [
  { key: null, label: "Everyone" },
  { key: "USER", label: "Learners" },
  { key: "EDITOR", label: "Educators" },
];
const SORT_OPTIONS = [
  { value: "relevance", label: "Most relevant" },
  { value: "most_followed", label: "Most followed" },
  { value: "recent", label: "Recently joined" },
  { value: "most_active", label: "Most active" },
  { value: "alphabetical", label: "Alphabetical" },
];

const formatNumber = (value) => Number(value || 0).toLocaleString();

const PeopleSkeleton = () => (
  <div className={styles.peopleGrid} aria-label="Loading people">
    {Array.from({ length: 6 }).map((_, index) => (
      <div key={index} className={styles.skeletonCard}>
        <span className={styles.skeletonAvatar} />
        <div className={styles.skeletonCopy}>
          <span className={styles.skeletonLine} />
          <span className={`${styles.skeletonLine} ${styles.shortLine}`} />
          <span className={`${styles.skeletonLine} ${styles.longLine}`} />
        </div>
      </div>
    ))}
  </div>
);

const PersonCard = memo(({ user, onOpen, isDemo = false }) => {
  const role = user.badge || (user.user_role === "EDITOR" ? "Educator" : "Learner");
  const primarySpecialty = user.specialties?.[0];
  const followersCount = Number(user.followersCount || 0);
  const followingCount = Number(user.followingCount || 0);
  const mutualFollowers = Number(user.mutualFollowers || 0);
  const stats = user.stats || {};
  const hasSocialProof = followersCount > 0 || followingCount > 0 || mutualFollowers > 0;
  const openProfile = () => onOpen(getUserProfilePath(user.user_code));
  const keyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openProfile();
    }
  };

  return (
    <article
      className={styles.personCard}
      role="button"
      tabIndex={0}
      aria-label={`Open ${user.name || "user"}'s profile`}
      onClick={openProfile}
      onKeyDown={keyDown}
    >
      <div className={styles.personTop}>
        <UserAvatar user={user} size={60} />
        <div className={styles.personIdentity}>
          <div className={styles.nameRow}>
            <strong>{user.name || "IllnessRx member"}</strong>
            {user.isVerified && <Icon icon="BsCheck2Circle" size={14} className={styles.verified} />}
          </div>
          {user.username && <span className={styles.username}>@{user.username}</span>}
          <div className={styles.roleLine}>
            <span className={`${styles.roleBadge} ${role === "Educator" ? styles.educatorBadge : styles.learnerBadge}`}>{role}</span>
            {user.educationLevel && <span className={styles.educationLevel}>{user.educationLevel}</span>}
            {primarySpecialty && <span className={styles.specialty}>{primarySpecialty}</span>}
          </div>
        </div>
      </div>

      {user.bio && <p className={styles.bio}>{user.bio}</p>}

      <div className={styles.learningMeta}>
        {user.specialties?.slice(0, 2).map((specialty) => <span key={specialty} className={styles.specialtyChip}>{specialty}</span>)}
        {stats.questionsAnswered !== null && stats.questionsAnswered !== undefined && <span><Icon icon="MdOutlineQuiz" size={12} /> {formatNumber(stats.questionsAnswered)} answered</span>}
        {stats.accuracy !== null && stats.accuracy !== undefined && <span><Icon icon="AiOutlineStar" size={12} /> {stats.accuracy}% accuracy</span>}
        {user.activityStatus === "active" && <span className={styles.activeStatus}>Active this week</span>}
      </div>

      <div className={`${styles.personFooter} ${!hasSocialProof ? styles.personFooterMinimal : ""}`}>
        {hasSocialProof && <div className={styles.socialProof}>
          {followersCount > 0 && <span><Icon icon="FiUsers" size={13} /> {formatNumber(followersCount)} {followersCount === 1 ? "Follower" : "Followers"}</span>}
          {followingCount > 0 && <span><Icon icon="FiUsers" size={13} /> Following {formatNumber(followingCount)}</span>}
          {mutualFollowers > 0 && (
            <span><Icon icon="FiUsers" size={13} /> Followed by {mutualFollowers} {mutualFollowers === 1 ? "person" : "people"} you know</span>
          )}
        </div>}
        <div className={styles.followAction} onClick={(event) => event.stopPropagation()}>
          {isDemo ? <Button size="small" type={user.relationship === "following" ? "light" : "default"} text={user.relationship === "following" ? "Following" : "Follow"} /> : <FollowButton userCode={user.user_code} relationship={user.relationship} size="small" variant="outline" />}
        </div>
      </div>
    </article>
  );
});

const EmptyState = ({ hasFilters, onClear }) => (
  <div className={styles.emptyState}>
    <span className={styles.stateIcon}><Icon icon="FiSearch" size={22} /></span>
    <Title level={4}>{hasFilters ? "No learners match your search" : "No learners found"}</Title>
    <Text>
      {hasFilters ? "Try another search or clear the active filters." : "There are no learners to show right now."}
    </Text>
    {hasFilters && <div className={styles.emptyActions}><Button type="default" text="Clear search" onClick={onClear} /><Button type="light" text="Browse all" onClick={onClear} /></div>}
  </div>
);

const ErrorState = ({ onRetry }) => (
  <div className={styles.emptyState}>
    <span className={styles.stateIcon}><Icon icon="BiSad" size={22} /></span>
    <Title level={4}>We could not load people</Title>
    <Text>Please check your connection and try again.</Text>
    <Button type="default" text="Retry" onClick={onRetry} />
  </div>
);

const People = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const [query, setQuery] = useState(initialQuery);
  const [role, setRole] = useState(searchParams.get("role") || null);
  const [sort, setSort] = useState(searchParams.get("sort") || "relevance");
  const canUseDemoData = process.env.NODE_ENV !== "production";
  const [useDemoData, setUseDemoData] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [requestError, setRequestError] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [trigger, { isFetching }] = useLazySearchPeopleQuery();
  const debounceRef = useRef(null);
  const loadMoreRef = useRef(null);
  const listRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const pageRef = useRef(1);

  const updateUrl = useCallback((nextQuery, nextRole, nextSort = sort) => {
    const next = {};
    if (nextQuery) next.q = nextQuery;
    if (nextRole) next.role = nextRole;
    if (nextSort && nextSort !== "relevance") next.sort = nextSort;
    setSearchParams(next, { replace: true });
  }, [setSearchParams, sort]);

  const load = useCallback(async ({ reset, queryValue = query, roleValue = role, sortValue = sort } = {}) => {
    const nextPage = reset ? 1 : pageRef.current + 1;
    if (!reset) setIsLoadingMore(true);
    setRequestError(false);
    const result = await trigger({
      q: queryValue || undefined,
      role: roleValue || undefined,
      sort: sortValue,
      page: nextPage,
    });
    const response = result?.data;
    if (response) {
      setItems((current) => reset ? response.data || [] : [...current, ...(response.data || [])]);
      setCount(response.count || 0);
      pageRef.current = nextPage;
    } else if (reset) {
      setRequestError(true);
    }
    setHasLoaded(true);
    setIsLoadingMore(false);
    loadingMoreRef.current = false;
  }, [query, role, sort, trigger]);

  const scheduleSearch = useCallback(() => {
    window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => load({ reset: true }), 350);
  }, [load]);

  useEffect(() => {
    scheduleSearch();
    return () => window.clearTimeout(debounceRef.current);
  }, [query, role, scheduleSearch, sort]);

  const visibleItems = useDemoData ? demoPeople.filter((user) => {
    const matchesRole = !role || user.user_role === role;
    const search = query.toLowerCase();
    const matchesQuery = !search || [user.name, user.username, user.bio, ...(user.specialties || [])].filter(Boolean).join(" ").toLowerCase().includes(search);
    return matchesRole && matchesQuery;
  }) : items;
  const hasMore = !useDemoData && items.length < count;
  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel || !hasMore || isFetching || isLoadingMore) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || loadingMoreRef.current) return;
      loadingMoreRef.current = true;
      load({ reset: false });
    }, { root: listRef.current, rootMargin: "260px" });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isFetching, isLoadingMore, load]);

  const changeQuery = (event) => {
    const value = event.target.value;
    setQuery(value);
    updateUrl(value, role);
  };
  const changeRole = (nextRole) => {
    setRole(nextRole);
    updateUrl(query, nextRole);
  };
  const changeSort = (event) => {
    const next = event.target.value;
    setSort(next);
    updateUrl(query, role, next);
  };
  const clearFilters = () => {
    setQuery("");
    setRole(null);
    setSort("relevance");
    updateUrl("", null, "relevance");
  };
  const onSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      window.clearTimeout(debounceRef.current);
      load({ reset: true, queryValue: event.currentTarget.value });
    }
    if (event.key === "Escape" && query) {
      setQuery("");
      updateUrl("", role);
    }
  };
  const isInitialLoading = !hasLoaded && !requestError;
  const isRefreshing = !useDemoData && hasLoaded && isFetching && !isLoadingMore;

  return (
    <PageLayout label={<Title level={2}>Community</Title>}>
      <main className={styles.peoplePage}>
        <header className={styles.peopleHeader}>
          <div>
            <Title level={3} className={styles.peopleTitle}>Find People</Title>
            <Text className={styles.peopleDescription}>Discover learners and educators in the IllnessRx community.</Text>
          </div>
        </header>

        <section className={styles.controls} aria-label="Search people">
          <div className={styles.searchInputWrap}>
            <Icon icon="FiSearch" size={18} className={styles.searchIcon} />
            <input
              value={query}
              onChange={changeQuery}
              onKeyDown={onSearchKeyDown}
              placeholder="Search learners and educators..."
              aria-label="Search learners and educators"
            />
            {query && <button type="button" className={styles.clearSearch} aria-label="Clear search" onClick={() => { setQuery(""); updateUrl("", role); }}><Icon icon="IoMdClose" size={17} /></button>}
          </div>
          <div className={styles.toolbar}>
            <div className={styles.segmentedControl} role="group" aria-label="Filter people by role">
              {ROLES.map((item) => (
                <button
                  type="button"
                  key={item.label}
                  className={role === item.key ? styles.activeSegment : ""}
                  aria-pressed={role === item.key}
                  onClick={() => changeRole(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <label className={styles.sortControl}>Sort <select value={sort} onChange={changeSort}>{SORT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
          </div>
        </section>

        <section ref={listRef} className={styles.listSection} aria-live="polite">
          {isRefreshing && <span className={styles.refreshing}>Updating results</span>}
          {useDemoData ? visibleItems.length === 0 ? <EmptyState hasFilters={Boolean(query || role)} onClear={clearFilters} /> : <div className={styles.peopleGrid}>{visibleItems.map((user) => <PersonCard key={user.user_code} user={user} onOpen={navigate} isDemo />)}</div> : isInitialLoading ? <PeopleSkeleton /> : requestError ? <ErrorState onRetry={() => load({ reset: true })} /> : items.length === 0 ? <EmptyState hasFilters={Boolean(query || role)} onClear={clearFilters} /> : <>
            <div className={styles.peopleGrid}>{items.map((user) => <PersonCard key={user.user_code} user={user} onOpen={navigate} />)}</div>
            {hasMore && <div ref={loadMoreRef} className={styles.scrollSentinel}>{isLoadingMore && "Loading more…"}</div>}
          </>}
        </section>
        {canUseDemoData && <DemoDataToggle value={useDemoData} onChange={setUseDemoData} label="People data" />}
      </main>
    </PageLayout>
  );
};

export default People;
